import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactHtmlParser from 'html-react-parser';

import { IconUser, IconLock, palette } from '@mirinae/react-ui';
import { Validation } from '@mirinae/js-utils';
import { authAPI } from '@mirinae/react-auth';
import { useAppVersionStore } from '@mirinae/stores/appVersion';

//layouts
import {
    FormRow, FormLabel, FormEmailWrapper, FormPasswordWrapper, FormIcon, FormError,
    AuthPopupLayer, PopupTitle, PopupTitleWrapper, PopupTitleDesc,
    AuthColDiv, AuthButton, AuthColForms, AuthPopupWrapper, FormBottom, InputContainer,
} from '@mirinae/explorer/components/userauth/AuthPopupLayer';
import CheckBoxForm from '@mirinae/explorer/components/ui/CheckBoxForm';
import { LoginColButtons, LinkForgot, LoginColLinks, 
    LoginRightBox, FirstRowMessage, SecondRowMessage, 
    ThirdRowMessage } from '@mirinae/explorer/components/userauth/LoginLayer'; 
import { LogoHolder } from '@mirinae/explorer/components/userauth/UserAuthComponent';
import SnsButtonsGroupContainer from '@mirinae/explorer/containers/userauth/SnsButtonsGroupContainer';
import { ReactComponent as IconMirinaeLogo } from '@mirinae/explorer/components/assets/icon-mirinae-logo.svg';
import { ReactComponent as IconVisible } from '@mirinae/explorer/components/assets/visibility.svg';


import { explorerPaths, authPaths, navigateExternal } from '@mirinae/shared/defines/paths';
import GTM from '@mirinae/shared/apis/GoogleAnalytics';
import { Event } from '@mirinae/explorer/modules/apis/GA3';
import Localization from '@mirinae/shared/il8n';

const LoginLayerContainer = props => {
    const defaultFormData = {
        userName: '',
        email: '',
        password: '',
        remember: props.remember || false,
    };
    const defaultError = {
        email: '',
        password: '',
        correcting: false,
    };
    const defaultFocuseState = {
        email: false, password: false,
    };
    const [formData, setFormData] = useState(defaultFormData);
    const [errors, setErrors] = useState(defaultError);
    const [failMessage, setFailMessage] = useState('');
    const [showSendVerifyEmail, setShowSendVerifyEmail] = useState(false);
    const [visiblePW, setVisiblePW] = useState(false);
    const [formFocus, setFormFocus] = useState(defaultFocuseState);
    const isApp = useAppVersionStore(state => state.isApp);
    const currentLanguage = Localization.getLanguage();
    authAPI.setHistory(window.history);

    // const preventScroll = () => {
    //     return false;
    // };

    useEffect(() => {
        setFormData(defaultFormData);
        setErrors(defaultError);
        setFailMessage('');
        setShowSendVerifyEmail(false);
        // window.addEventListener('scroll', preventScroll, false);
        // return (() => {
        //     window.removeEventListener('scroll', preventScroll);
        // });    useEffect(() => {

        // check for errors in query params, possiblly from mirinae-react-auth SNS login completion checking
        const qparams = new URLSearchParams(window.location.search);
        const errorParam = qparams.get('err');
        if (errorParam) {
            try {
                const error = JSON.parse(errorParam);
                const loginType = (error.loginType || 'Unknown').replace(/^(.)(.*)/, (m, g1, g2) => `${g1.toUpperCase()}${g2}`).replace('Local', 'email/password');
                setFailMessage(error.errCode ? Localization[`err-${error.errCode}`].replace('{loginType}', loginType) : error.message);
            } catch (e) {
                console.log('exception parsing err qparam', e);
            }
        }

        GTM.pageView('login', { },  'explorer');
    }, []);

    useEffect(() => {
        setFormData({ ...formData, remember: props.remember || false });
    }, [props.remember]);

    // form validation 
    const validate = () => {
        const currentErrors = {};

        if (!formData.email) {
            currentErrors.email = Localization['fe-required'];
        } else if (Validation.invalidEmail(formData.email)) {
            currentErrors.email = Localization['fe-invalidEmail'];
        } else {
            currentErrors.email = '';
        }

        if (!formData.password) {
            currentErrors.password = Localization['fe-required'];
        } else {
            currentErrors.password = '';
        }

        currentErrors.correcting = Boolean(currentErrors.email || currentErrors.password);
        setErrors(currentErrors);

        return !currentErrors.correcting;
    };

    // handle change on form value
    const inputFieldChanged = (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData({ ...formData, [event.target.name]: value });
        if (errors.correcting)
            validate();
        if (failMessage !== '')
            setFailMessage('');
    };

    const inputFormBlur = (event) => {
        if (errors[event.target.name]) {
            validate();
        }
        setFormFocus(defaultFocuseState);
    };

    const inputFormFocus = (event) => {
        setFormFocus({ ...defaultFocuseState, [event.target.name]: true });
    };

    const onFailure = (error) => {
        const loginType = (error.loginType || 'Unknown').replace(/^(.)(.*)/, (m, g1, g2) => `${g1.toUpperCase()}${g2}`).replace('Local', 'email/password');
        setFailMessage(error.errCode ? Localization[`err-${error.errCode}`].replace('{loginType}', loginType) : error.message);
        if (error.errCode === 'A009') {
            setShowSendVerifyEmail(true);
        }
    };

    const loginClicked = () => {
        if (!validate()) return;
        setFailMessage(''); setShowSendVerifyEmail(false);
        GTM.userAction('click-email-login', { 'email': formData.email }, 'explorer');
        authAPI.loginLocal(formData).then(response => {
            if (response.data) {
                const { user } = response.data;
                // check for redirects
                const qparams = new URLSearchParams(window.location.search);
                const redirect = qparams.get('redirect');
                // if redirect on URL, disable loginSucceeded within-domain redirect & redirect via window.location.href assign
                authAPI.loginSucceeded(user, response.error, formData.remember, explorerPaths.home, !redirect, onFailure);
                if (props.loginCallbak) props.loginCallbak();
                if (redirect) {
                    console.log('redirect from loginClicked', redirect);
                    navigateExternal(redirect, { replace: true });
                }
                GTM.userAction('login', { type: 'email' }, 'explorer');
            } else {
                onFailure(response.error);
            }
            Event('Login', 'email', isApp ? 'app' : 'web');
        });
    };

    const resendVerificationEmail = async () => {
        setFailMessage('');
        setShowSendVerifyEmail(false);
        await fetch('/api/auth/signup/send-verification', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: formData.email }),
        });
    };

    return (
        <>
            <AuthPopupLayer theme={props.container}>
                <AuthPopupWrapper>
                    <LogoHolder>
                        <IconMirinaeLogo />
                    </LogoHolder>
                    {!props.container && (
                        <PopupTitleWrapper>
                            <PopupTitle>{ReactHtmlParser(Localization['login-leftbox-welcome-v1.1'])}</PopupTitle>
                            {/* <PopupTitleDesc>{ReactHtmlParser(Localization['login-leftbox-subwelcome-v1.1'])}</PopupTitleDesc> dropped for now, a simple Log in */}
                        </PopupTitleWrapper>
                    )}
                    <SnsButtonsGroupContainer
                        {...props}
                        onFailure={onFailure}
                        isApp={isApp}
                        formData={formData}
                    />
                    <AuthColDiv>{ReactHtmlParser(Localization['or-text'])}</AuthColDiv>

                    <AuthColForms>
                        <FormRow>
                            <InputContainer>
                                <FormLabel htmlFor="login-email">{ReactHtmlParser(Localization['su-email'])}</FormLabel>
                                <FormEmailWrapper isFocused={formFocus.email === true}>
                                    <FormIcon>
                                        <IconUser width="10" height="11" color={formFocus.email ? palette.main.darken[1] : palette.gray[4]} />
                                    </FormIcon>
                                    <input
                                        type="email" name="email" maxLength="100"
                                        value={formData.email} id="login-email"
                                        onChange={inputFieldChanged}
                                        onBlur={inputFormBlur} onFocus={inputFormFocus} placeholder="Enter Your email address" />
                                </FormEmailWrapper>
                                <FormError>
                                    {errors.email.length > 0 && errors.email}
                                </FormError>
                            </InputContainer>
                            <InputContainer>
                                <FormLabel htmlFor="login-pw">{Localization['su-password']}</FormLabel>
                                <FormPasswordWrapper isFocused={formFocus.password === true}>
                                    <FormIcon><IconLock color={formFocus.password ? palette.main.darken[1] : palette.gray[4]} /></FormIcon>
                                    <input
                                        type={visiblePW ? 'text' : 'password'} name="password" maxLength="20"
                                        value={formData.password} id="login-pw"
                                        onChange={inputFieldChanged}
                                        onBlur={inputFormBlur} onFocus={inputFormFocus}
                                        onKeyPress={e => { if (e.key === 'Enter') loginClicked(); }} />
                                    <FormIcon style={{ marginRight: 0 }} onClick={() => setVisiblePW(!visiblePW)}><IconVisible /></FormIcon>
                                </FormPasswordWrapper>
                                <FormError>
                                    {errors.password && errors.password}
                                    {failMessage}
                                    { showSendVerifyEmail && (
                                        <span style={{ cursor: 'pointer', textDecoration: 'underline', marginLeft: 4 }} onClick={resendVerificationEmail}>
                                            {Localization['signup-send-again']}
                                        </span>
                                    )}
                                </FormError>
                            </InputContainer>
                        </FormRow>
                    </AuthColForms>
                    <FormBottom>
                        <LoginColButtons>
                            <CheckBoxForm name="remember" value={formData.remember} onClick={_value => setFormData({ ...formData, remember: _value })}>
                                {Localization['login-leftbox-keepsignedin']}
                            </CheckBoxForm>
                            <LinkForgot reloadDocument to={authPaths.resetPassword}>{Localization['auth-forgot']}</LinkForgot>
                        </LoginColButtons>
                        <AuthButton>
                            <button type="button" disabled={formData.email === '' || formData.password === ''} onClick={loginClicked}>{Localization['rp-login']}</button>
                        </AuthButton>
                        <LoginColLinks>
                            {Localization.noAccount}
                            <Link reloadDocument to={authPaths.signup} replace onClick={() => GTM.userAction('click-signup')}>{Localization['auth-signup']}!</Link>
                        </LoginColLinks>
                    </FormBottom>
                </AuthPopupWrapper>
            </AuthPopupLayer>
        </>
    );
};

export default LoginLayerContainer;
